import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { useLocation, useParams } from "react-router-dom";

import { PasswordStrength, Password, Text, Select } from "./Forms";
import { ModalCancelButton, ModalSaveButton, Loading } from ".";

import { Auth } from "../api";
import axios from "../api/axios";
import { validatePassword } from "../helpers/Validate";

function PasswordReset() {
  const location = useLocation();
  const params = useParams();
  const [buttonVariant, setButtonVariant] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [questions, setQuestions] = useState(undefined);

  const mustAnswerSecretQuestion = useMemo(
    () => !!(questions?.length && questions[0].label),
    [questions]
  );

  const validationSchema = useCallback(
    () =>
      Yup.object().shape({
        password: validatePassword,
        password_confirmation: Yup.string()
          .oneOf(
            [Yup.ref("password")],
            "Please enter a confirm password that matches the new password"
          )
          .required("Please enter a confirm password"),
        question: Yup.string().test({
          message: "Please select a question",
          test: (value) => !mustAnswerSecretQuestion || value,
        }),
        answer: Yup.string().test({
          message: "Please enter an answer",
          test: (value) => !mustAnswerSecretQuestion || !!value?.length,
        }),
      }),
    [mustAnswerSecretQuestion]
  );

  const token = useMemo(() => params?.token || "", [params]);

  const email = useMemo(
    () => location.search?.split("=")?.pop() || "",
    [location]
  );

  const onSubmit = useCallback(
    async (values, { setErrors }) => {
      try {
        setErrors({});
        setButtonVariant("saving");
        if (!mustAnswerSecretQuestion) {
          delete values.question;
          delete values.answer;
        }

        const { data } = await Auth.passwordReset(values);
        setButtonVariant("success");
        setSuccessMessage(data?.status || "Your password has been reset!");
      } catch (error) {
        setButtonVariant("error");
        setErrors(error?.errors || {});
      }
    },
    [mustAnswerSecretQuestion]
  );

  const initialValues = useMemo(
    () => ({
      token,
      email,
      password: "",
      password_confirmation: "",
      question: mustAnswerSecretQuestion ? questions[0].id : undefined,
      question_label: mustAnswerSecretQuestion ? questions[0].label : undefined,
      answer: "",
    }),
    [token, email, questions, mustAnswerSecretQuestion]
  );

  useEffect(() => {
    const getQuestion = async () => {
      try {
        const res = await axios.get("api/auth/security_questions", {
          params: { email },
        });
        setQuestions(
          res.data.data.map((question) => ({
            label: question.question,
            value: question.question,
            id: question.id,
          }))
        );
      } catch (error) {
        setQuestions([]);
      }
    };
    if (email) {
      getQuestion();
    }
  }, [email]);

  if (questions === undefined) {
    return <Loading />;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ handleSubmit, resetForm, values, setValues, errors }) => (
        <form
          id="settingsPasswordForm"
          className="form change-password__content"
          onSubmit={handleSubmit}
        >
          <div className="form__wrapper">
            <h1 className="white">Reset Password</h1>
            <div className="panel">
              <div className="panel__content form__block">
                <input
                  id="username"
                  value={values.email}
                  name="username"
                  style={{ display: "none" }}
                />
                {mustAnswerSecretQuestion &&
                  (questions.length === 1 ? (
                    <div className="flex-row form__row">
                      <div className="form__password flex-row__stretch">
                        <Text
                          label={questions[0].label}
                          name="answer"
                          placeholder="Answer"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-row form__row">
                        <div className="form__password flex-row__stretch">
                          <Select
                            label="Select a security question"
                            name="question_label"
                            options={questions}
                            onChange={(question) =>
                              setValues((prev) => ({
                                ...prev,
                                question: question.id,
                                question_label: question.label,
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="flex-row form__row">
                        <div className="form__password flex-row__stretch">
                          <Text
                            name="answer"
                            placeholder={`Answer ${
                              values.question_label || ""
                            }`}
                            disabled={!values.question}
                          />
                        </div>
                      </div>
                    </>
                  ))}
                <div className="flex-row form__row">
                  <div className="form__password flex-row__stretch">
                    <Password
                      label="New Password"
                      name="password"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <div className="flex-row form__row">
                  <Password
                    label="Confirm Password"
                    name="password_confirmation"
                    autoComplete="new-password"
                    className="flex-row__stretch form__confirm_password"
                  />
                </div>
                <div className="flex-row form__row">
                  <PasswordStrength />
                </div>
                {!!successMessage && (
                  <div className="flex-row form__row">
                    <div className="remodal__validation validation">
                      <div className="alert-box success ajax-submit-alert">
                        <span className="validate-message">
                          {successMessage}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div
                  className="flex-row form__finalise-changes"
                  style={{ marginTop: 40 }}
                >
                  <ModalCancelButton action="close" onClick={resetForm}>
                    Cancel
                  </ModalCancelButton>
                  <ModalSaveButton
                    type="submit"
                    variant={buttonVariant}
                    onClick={handleSubmit}
                    onTryAgain={handleSubmit}
                    errorMessage={errors?.email || undefined}
                  >
                    Save Changes
                  </ModalSaveButton>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}

export default PasswordReset;
